import {
  AXIS_LABEL_HEIGHT_PX,
  AXIS_LABEL_PADDING_X_PX,
  CROSSHAIR_DASH_PX,
  CROSSHAIR_LINE_WIDTH_PX,
  TIME_AXIS_HEIGHT_PX,
} from "../constants";
import { axisFont } from "../text/axisFont";
import { formatPrice } from "../axes/priceFormat";
import { drawAxisLabel } from "./axisLabel";
import { clampBadgeY, drawPriceBadge, priceBadgeRect } from "./priceBadge";
import { paneAtY } from "../indicators/indicatorPanes";
import { indexToTime } from "../drawings/timeIndex";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Crosshair at `point`. With grid snap x jumps to the centre of the bar under
 * it (also in the empty space right of the last bar); without it x and the
 * time follow the pointer smoothly. The price always follows the pointer on
 * the scale of the pane under it (an oscillator pane has its own). `index` and
 * `bar`, for the OHLC, are always of the bar under the pointer.
 *
 * @param {object} params
 * @param {{ x: number, y: number } | null} params.point - CSS px of the chart
 * @param {{ pane: object, indicatorPanes?: object[] }} params.layout - of the frame
 * @param {object} params.timeScale
 * @param {object} params.priceScale - of the price pane
 * @param {object} params.store - BarStore
 * @param {number} params.barDurationMs
 * @param {boolean} params.isSnapEnabled - grid snap
 * @returns {{
 *   x: number, y: number, index: number, time: number, price: number, bar: object|null, paneId: string,
 * } | null} null outside the panes or without bars; paneId: MAIN_PANE_ID or the key of the oscillator
 */
export const resolveCrosshair = ({ point, layout, timeScale, priceScale, store, barDurationMs, isSnapEnabled }) => {
  if (!point || !store.length || point.x < 0 || point.x >= layout.pane.width) return null;

  const pane = paneAtY(layout, point.y);

  if (!pane) return null;

  const exactIndex = timeScale.xToIndex(point.x);
  const index = Math.round(exactIndex);

  return {
    x: isSnapEnabled ? timeScale.indexToX(index) : point.x,
    y: point.y,
    index,
    time: isSnapEnabled ? store.timeAt(index, barDurationMs) : indexToTime(exactIndex, store, barDurationMs),
    price: (pane.priceScale || priceScale).yToPrice(point.y - pane.top),
    bar: store.bars[index] || null,
    paneId: pane.id,
  };
};

// A line of odd device width is centred on a pixel centre, of even width on a pixel edge
const snapLine = (position, lineWidth, pixelRatio) =>
  (Math.floor(position * pixelRatio) + (lineWidth % 2) / 2) / pixelRatio;

const drawLines = (textLayer, { x, y }, { width, height }, color) => {
  const { context, pixelRatio } = textLayer;
  const lineWidth = Math.max(1, Math.floor(CROSSHAIR_LINE_WIDTH_PX * pixelRatio));
  const lineX = snapLine(x, lineWidth, pixelRatio);
  const lineY = snapLine(y, lineWidth, pixelRatio);

  context.save();
  context.beginPath();
  context.rect(0, 0, width, height);
  context.clip();
  context.lineWidth = lineWidth / pixelRatio;
  context.strokeStyle = color;
  context.setLineDash(CROSSHAIR_DASH_PX);
  context.beginPath();
  context.moveTo(lineX, 0);
  context.lineTo(lineX, height);
  context.moveTo(0, lineY);
  context.lineTo(width, lineY);
  context.stroke();
  context.restore();
};

/**
 * Draws the dashed crosshair lines (the vertical one through all panes), the
 * price badge on the price axis (full pair precision, E9, or the precision
 * of the oscillator pane; shaped like all price badges) and the time label
 * on the time axis.
 *
 * @param {object} params
 * @param {object} params.textLayer - the crosshair layer
 * @param {(text: string, font: string) => number} params.measure - cached, shared with the text layer
 * @param {object} params.crosshair - from resolveCrosshair
 * @param {{ pane: object, plotHeight: number, indicatorPanes: object[], priceAxisWidth: number }} params.layout
 * @param {string} params.timeText - from TimeAxis.formatCrosshairTime
 * @param {number} params.decimals - price precision of the pair
 * @param {{ colors: object, fontFamily: string }} params.theme
 */
export const drawCrosshair = ({ textLayer, measure, crosshair, layout, timeText, decimals, theme }) => {
  const { pane, plotHeight, priceAxisWidth } = layout;
  const crosshairPane = paneAtY(layout, crosshair.y) || { top: 0, height: pane.height };
  const { colors } = theme;
  const font = axisFont(theme.fontFamily);
  const style = { font, background: colors.crosshairLabel.css, color: colors.crosshairLabelText.css };
  const timeWidth = measure(timeText, font) + AXIS_LABEL_PADDING_X_PX * 2;
  const timeX = clamp(crosshair.x, timeWidth / 2, Math.max(timeWidth / 2, pane.width - timeWidth / 2));

  // An oscillator pane may be partly scrolled away: the badge stays in its visible part
  const badgeTop = crosshairPane.visibleTop ?? crosshairPane.top;
  const badgeArea = { height: crosshairPane.visibleHeight ?? crosshairPane.height };
  const badgeY = clampBadgeY(crosshair.y - badgeTop, badgeArea) + badgeTop;

  drawLines(textLayer, crosshair, { width: pane.width, height: plotHeight }, colors.crosshair.css);

  drawPriceBadge(
    textLayer,
    priceBadgeRect(badgeY, pane, priceAxisWidth),
    formatPrice(crosshair.price, crosshairPane.decimals ?? decimals),
    style,
  );
  drawAxisLabel(
    textLayer,
    {
      rect: {
        x: timeX - timeWidth / 2,
        y: plotHeight + (TIME_AXIS_HEIGHT_PX - AXIS_LABEL_HEIGHT_PX) / 2,
        width: timeWidth,
        height: AXIS_LABEL_HEIGHT_PX,
      },
      text: timeText,
      textX: timeX,
      align: "center",
    },
    style,
  );
};
